import React, { useState, useEffect, useCallback } from "react";

import {
  listOpcuaServers,
  createOpcuaServer,
  updateOpcuaServer,
  deleteOpcuaServer,
  getOpcuaServerState,
  reconnectOpcuaServer,
  opcuaClientCertificateUrl,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import Select from "../../../../components/Select";
import PasswordInput from "../../../../components/PasswordInput";
import plusIcon from "../../../../assets/plus.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import moreIcon from "../../../../assets/more-horiz.svg";
import {
  DEFAULT_HISTORY_TEMPLATE,
  HistorianDefaultsFields,
  buildHistorianTemplatePayload,
  templateFormFromRecord,
} from "./historian-defaults";

const POLL_INTERVAL = 10000;
const STUCK_OFFLINE_MIN_MS = 5 * 60 * 1000;
const STUCK_OFFLINE_MAX_MS = 60 * 60 * 1000;

const SECURITY_MODES = [
  { value: "none", label: "None" },
  { value: "sign", label: "Sign" },
  { value: "sign_and_encrypt", label: "Sign and Encrypt" },
];

const SECURITY_POLICIES = [
  { value: "None", label: "None" },
  { value: "Basic256Sha256", label: "Basic256Sha256" },
  { value: "Aes128_Sha256_RsaOaep", label: "Aes128_Sha256_RsaOaep" },
  { value: "Aes256_Sha256_RsaPss", label: "Aes256_Sha256_RsaPss" },
];

const AUTH_MODES = [
  { value: "anonymous", label: "Anonymous" },
  { value: "username", label: "Username / Password" },
];

const STATUS_COLORS = {
  online: { bg: "rgba(76,175,80,0.12)", color: "rgba(76,175,80,0.95)", border: "rgba(76,175,80,0.25)", dot: "#4caf50" },
  connecting: { bg: "rgba(255,193,7,0.12)", color: "rgba(255,193,7,0.95)", border: "rgba(255,193,7,0.25)", dot: "#ffc107" },
  offline: { bg: "rgba(158,158,158,0.12)", color: "rgba(200,200,200,0.95)", border: "rgba(158,158,158,0.25)", dot: "#9e9e9e" },
  error: { bg: "rgba(244,67,54,0.12)", color: "rgba(244,67,54,0.95)", border: "rgba(244,67,54,0.25)", dot: "#f44336" },
};

const StatusDot = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.offline;
  return (
    <span title={status} style={{ width: 8, height: 8, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
  );
};

const errorMessage = (err, fb) => {
  const m = String(err?.message || err).trim();
  return m || fb;
};

// Offline-after-connection heuristic: a server that recently worked has
// dropped (e.g. it restarted and invalidated our session). The backend
// watchdog force-rebuilds the connection after ~5 min, so we just reassure
// the operator it's auto-recovering. Windowed 5–60 min: below 5 min
// gopcua's own reconnect is still trying; above 60 min the server is most
// likely just down for good.
const isLikelyStuckOffline = (st) => {
  if (!st || st.status !== "offline" || !st.last_connected_at) return false;
  const ms = Date.now() - new Date(st.last_connected_at).getTime();
  return ms > STUCK_OFFLINE_MIN_MS && ms < STUCK_OFFLINE_MAX_MS;
};

// ── Server create/edit modal ────────────────────────────────────────

// Recording is on by default (stored-by-default) — historian fields mirror
// the tag editor's Historian tab / mqtt-brokers' subscription defaults.
const INITIAL_HISTORY_TEMPLATE = {
  historianEnabled: true,
  ...DEFAULT_HISTORY_TEMPLATE,
};

const INITIAL_SERVER = {
  name: "",
  endpointUrl: "opc.tcp://",
  securityMode: "none",
  securityPolicy: "None",
  authMode: "anonymous",
  username: "",
  password: "",
  // UI-only intent flag: explicit "remove existing password" on edit.
  // Lets the user clear a stored credential without having to type a
  // new one and lets us distinguish blank-means-keep from blank-means-clear.
  clearPassword: false,
  serverCertPem: "",
  ...INITIAL_HISTORY_TEMPLATE,
};

const ServerModal = ({ editing, onClose, onSaved }) => {
  const [form, setForm] = useState(() => {
    if (!editing) return INITIAL_SERVER;
    return {
      name: editing.name || "",
      endpointUrl: editing.endpoint_url || "opc.tcp://",
      securityMode: editing.security_mode || "none",
      securityPolicy: editing.security_policy || "None",
      authMode: editing.auth_mode || "anonymous",
      username: editing.username || "",
      password: "", // never returned by API
      clearPassword: false,
      serverCertPem: editing.server_cert_pem || "",
      historianEnabled: editing.historian_enabled != null ? !!editing.historian_enabled : true,
      ...templateFormFromRecord(editing),
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const notify = useNotify();
  const isEdit = !!editing;

  const set = useCallback((field) => (e) => {
    const value = typeof e === "string" ? e : (e?.target?.type === "checkbox" ? e.target.checked : e.target.value);
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Mode and policy must agree on "is security on". Snap policy when
  // mode toggles so the form can't submit an invalid pair.
  const setSecurityMode = useCallback((value) => {
    setForm((prev) => ({
      ...prev,
      securityMode: value,
      securityPolicy: value === "none"
        ? "None"
        : (prev.securityPolicy === "None" ? "Basic256Sha256" : prev.securityPolicy),
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Trim free-text fields: backend stores Name/Username verbatim and
    // uniqueness checks are case-insensitive but not whitespace-insensitive,
    // so "  foo  " would slip past a duplicate check on "foo".
    const isUsernameAuth = form.authMode === "username";
    const payload = {
      name: form.name.trim(),
      endpoint_url: form.endpointUrl.trim(),
      security_mode: form.securityMode,
      security_policy: form.securityPolicy,
      auth_mode: form.authMode,
      username: isUsernameAuth ? form.username.trim() : "",
      server_cert_pem: form.serverCertPem.trim(),
      // Historian defaults seeded onto this server's imported tags. Always
      // sent so disabling (or changing) re-applies to existing tags.
      historian_enabled: form.historianEnabled,
      ...buildHistorianTemplatePayload(form),
    };

    // Password handling. The field is only visible when auth_mode is
    // "username" — when it's not, form.password is irrelevant even if
    // the user typed something before switching auth mode, so we ignore
    // it here. On edit, blank means "keep existing" unless the operator
    // explicitly checked Remove (clearPassword) or switched off
    // username auth, both of which clear the stored secret.
    if (isEdit) {
      if (!isUsernameAuth) {
        payload.password = ""; // clear stale credential on auth switch
      } else if (form.password) {
        payload.password = form.password; // rotate
      } else if (form.clearPassword) {
        payload.password = ""; // explicit clear via UI
      }
      // else: omit — backend treats as no-op, preserves existing secret
    } else if (isUsernameAuth && form.password) {
      // Create: only attach a password when actually using username auth.
      // Empty string would be a no-op anyway (backend skips secret-save).
      payload.password = form.password;
    }

    try {
      if (isEdit) {
        await updateOpcuaServer(editing.id, payload);
      } else {
        await createOpcuaServer(payload);
      }
      notify({ type: "success", title: isEdit ? "Updated" : "Created", message: `Server "${payload.name || payload.endpoint_url}" ${isEdit ? "updated" : "created"}.` });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: isEdit ? "Update Failed" : "Create Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 560 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{isEdit ? `Edit: ${editing.name || editing.id}` : "New OPC UA Server"}</div>
            <div className="gwModalSubtitle">{isEdit ? "Update the server configuration." : "Configure a new OPC UA server connection."}</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Name</label>
            <input className="gwInput" placeholder="e.g. plant-floor-plc" value={form.name} onChange={set("name")} autoFocus={!isEdit} />
            <div className="gwHint">Display name. Must be unique (case-insensitive) if set.</div>
          </div>
          <div className="gwField">
            <label className="gwLabel">Endpoint URL</label>
            <input
              className="gwInput"
              placeholder="opc.tcp://wincc-host:4861"
              value={form.endpointUrl}
              onChange={set("endpointUrl")}
              required
              autoFocus={isEdit}
            />
            <div className="gwHint">Binary OPC UA endpoint. WinCC Runtime Professional uses port <code>4861</code> by default.</div>
          </div>
          <div className="gwFieldRow">
            <div className="gwField">
              <label className="gwLabel">Security Mode</label>
              <Select value={form.securityMode} onChange={setSecurityMode} options={SECURITY_MODES} />
            </div>
            <div className="gwField">
              <label className="gwLabel">Security Policy</label>
              <Select
                value={form.securityPolicy}
                onChange={set("securityPolicy")}
                options={form.securityMode === "none"
                  ? SECURITY_POLICIES.filter((p) => p.value === "None")
                  : SECURITY_POLICIES.filter((p) => p.value !== "None")}
              />
            </div>
          </div>
          <div className="gwField">
            <label className="gwLabel">Authentication</label>
            <Select value={form.authMode} onChange={set("authMode")} options={AUTH_MODES} />
          </div>
          {form.authMode === "username" && (
            <>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">Username</label>
                  <input className="gwInput" value={form.username} onChange={set("username")} required />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Password</label>
                  <PasswordInput
                    placeholder={isEdit && editing.has_password && !form.clearPassword ? "Leave blank to keep current" : ""}
                    value={form.password}
                    onChange={set("password")}
                    disabled={form.clearPassword}
                  />
                </div>
              </div>
              {isEdit && editing.has_password && (
                <div className="gwField">
                  <label className="gwLabel" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={form.clearPassword}
                      onChange={(e) => setForm((p) => ({
                        ...p,
                        clearPassword: e.target.checked,
                        // Wipe any typed value so the intent is unambiguous —
                        // checkbox wins over typed password.
                        password: e.target.checked ? "" : p.password,
                      }))}
                    />
                    <span>Remove existing password</span>
                  </label>
                  <div className="gwHint">Clears the stored password. Use when the server now accepts the username without one.</div>
                </div>
              )}
            </>
          )}
          {form.securityMode !== "none" && (
            <>
              <div className="gwField">
                <label className="gwLabel">Server Certificate (PEM)</label>
                <textarea
                  className="gwInput"
                  rows={3}
                  placeholder="-----BEGIN CERTIFICATE-----"
                  value={form.serverCertPem}
                  onChange={set("serverCertPem")}
                  required
                  style={{ fontFamily: "monospace", fontSize: 11 }}
                />
                <div className="gwHint">Required for secure connections. Control Seat matches this reviewed WinCC certificate during discovery and requires proof of its private key.</div>
              </div>
              <div className="gwHint">
                WinCC must trust Control Seat's public client certificate. {" "}
                <a href={opcuaClientCertificateUrl()} download="control-seat-opcua-client.pem">Download certificate</a>
              </div>
            </>
          )}
          <div className="gwField">
            <label className="gwHistToggle">
              <input
                type="checkbox"
                className="gwHistToggleInput"
                checked={form.historianEnabled}
                onChange={set("historianEnabled")}
              />
              <span className={`gwToggle ${form.historianEnabled ? "isOn" : ""}`} aria-hidden="true"><span /></span>
              Record history
            </label>
            <div className="gwHint">
              Records this server's tags to Control Seat's historian as values arrive. Applies to existing and future imported tags; changing it re-applies to all of this server's tags.
            </div>
          </div>
          {form.historianEnabled && <HistorianDefaultsFields form={form} set={set} />}
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting}>
            {submitting ? "Saving..." : isEdit ? "Save" : "Create Server"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────

export const OpcConnectionsPage = () => {
  const canConfigure = usePermission("configure");
  const [servers, setServers] = useState([]);
  const [states, setStates] = useState({}); // id → ServerState
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reconnecting, setReconnecting] = useState(null);
  const notify = useNotify();

  // Fan out state fetches with allSettled so a slow server can't block
  // the list render. Mirrors the MQTT page's pattern.
  const fetchAll = useCallback(async () => {
    try {
      const list = (await listOpcuaServers()) || [];
      setServers(list);
      const stateResults = await Promise.allSettled(
        list.map((s) => getOpcuaServerState(s.id).then((st) => [s.id, st]))
      );
      setStates((prev) => {
        // Preserve the previous /state value for any server whose fetch
        // rejected this cycle — a single transient blip otherwise repaints
        // a healthy server as offline (status defaults to offline when
        // states[id] is missing). Persistent failures will look stale, but
        // backend status reflects reality on the next successful fetch.
        const next = { ...prev };
        for (const r of stateResults) {
          if (r.status === "fulfilled") {
            const [id, st] = r.value;
            next[id] = st;
          }
        }
        // Drop entries for servers no longer in the list (prevents the
        // map growing unboundedly across deletes).
        const liveIds = new Set(list.map((s) => s.id));
        for (const id of Object.keys(next)) {
          if (!liveIds.has(id)) delete next[id];
        }
        return next;
      });
    } catch (err) {
      notify({ type: "error", title: "Error", message: errorMessage(err, "Failed to load servers") });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchAll]);

  const openMenu = (e, id) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuStyle({ position: "fixed", top: `${Math.round(r.bottom + 4)}px`, right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px` });
    setMenuOpen(menuOpen === id ? null : id);
  };

  // Force an immediate rebuild instead of waiting out the backend watchdog.
  // The response is the post-rebuild state; a full refresh follows so the
  // status settles (the new session may still be mid-handshake).
  const handleReconnect = async (s) => {
    setMenuOpen(null);
    setReconnecting(s.id);
    try {
      const st = await reconnectOpcuaServer(s.id);
      setStates((prev) => ({ ...prev, [s.id]: st }));
      // The rebuild is synchronous, so the returned state already reflects
      // the outcome: a failed dial lands in error/offline rather than
      // throwing. Surface that instead of a blanket success.
      if (st?.status === "online" || st?.status === "connecting") {
        notify({ type: "success", title: "Reconnecting", message: `Rebuilding connection to "${s.name || s.id}".` });
      } else {
        notify({ type: "error", title: "Reconnect Failed", message: st?.last_error_msg || `"${s.name || s.id}" is still unreachable.` });
      }
    } catch (err) {
      notify({ type: "error", title: "Reconnect Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setReconnecting(null);
      fetchAll();
    }
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      const menu = document.querySelector(".gwProjectMenu");
      if (menu?.contains(e.target)) return;
      setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteOpcuaServer(deleteTarget.id);
      notify({ type: "success", title: "Removed", message: `Server "${deleteTarget.name || deleteTarget.id}" deleted.` });
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      notify({ type: "error", title: "Delete Failed", message: errorMessage(err, "Unknown error") });
    } finally {
      setDeleting(false);
    }
  };

  const hasServers = servers.length > 0;

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">OPC UA</div>
            <div className="gwPageSubtitle">
              {hasServers ? `${servers.length} server${servers.length !== 1 ? "s" : ""} configured` : "Connect OPC UA servers, including WinCC Runtime Professional."}
            </div>
          </div>
          <button
            className="gwPrimaryBtn"
            type="button"
            onClick={() => setShowCreate(true)}
            disabled={!canConfigure}
            title={canConfigure ? undefined : "Requires configure permission"}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
            <span>Add Server</span>
          </button>
        </div>

        {loading && !hasServers && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>Loading servers...</div>
        )}

        {!loading && !hasServers && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No servers yet</div>
            <div className="gwEmptyBody">Add an OPC UA server to start subscribing to tag values from PLCs and SCADA devices.</div>
            <div className="gwEmptyActions">
              <button
                className="gwPrimaryBtn"
                type="button"
                onClick={() => setShowCreate(true)}
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                <span>Add Server</span>
              </button>
            </div>
          </div>
        )}

        {hasServers && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Servers</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {servers.map((s) => {
                const st = states[s.id];
                const status = st?.status || "offline";
                const colors = STATUS_COLORS[status] || STATUS_COLORS.offline;
                const stuck = isLikelyStuckOffline(st);
                return (
                  <div
                    key={s.id}
                    data-gw-resource="opcua_servers"
                    data-gw-id={s.id}
                    data-gw-name={s.name || undefined}
                  >
                    <div className="umUserRow">
                      <div
                        style={{
                          width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                          background: colors.bg,
                          border: `1px solid ${colors.border}`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 700, color: colors.color,
                        }}
                      >
                        UA
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className="umUserName">{s.name || s.id}</span>
                          <StatusDot status={status} />
                        </div>
                        <div className="umUserMeta">
                          {s.endpoint_url}
                          {s.auth_mode === "username" ? " · auth" : ""}
                          {s.security_mode && s.security_mode !== "none" ? ` · ${s.security_mode}` : ""}
                          {st && status === "online" && (
                            <> &middot; {st.monitored_count} monitored &middot; {st.messages_per_sec.toFixed(1)} msg/s
                              {st.messages_dropped > 0 && (
                                <span style={{ color: "var(--gw-danger)" }}> · {st.messages_dropped} dropped</span>
                              )}
                            </>
                          )}
                          {st?.last_error_msg && (
                            <span style={{ color: "var(--gw-danger)" }} title={st.last_error_msg}> &middot; {st.last_error_msg}</span>
                          )}
                        </div>
                        {stuck && (
                          <div className="umUserMeta" style={{ color: "var(--gw-warning, #ffc107)", marginTop: 2 }}>
                            Offline after a recent connection — auto-reconnecting (rebuilds the session every few minutes until it recovers).
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="gwIconButton"
                        style={{ flexShrink: 0 }}
                        onClick={(e) => openMenu(e, s.id)}
                      >
                        <img src={moreIcon} alt="More" className="gwIcon" style={{ width: 16, height: 16 }} />
                      </button>
                      {menuOpen === s.id && (
                        <div className="gwProjectMenu" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                          <button
                            className="gwProjectMenuItem"
                            type="button"
                            disabled={!canConfigure}
                            title={canConfigure ? undefined : "Requires configure permission"}
                            onClick={() => { setMenuOpen(null); setEditing(s); }}
                          >
                            Edit
                          </button>
                          <button
                            className="gwProjectMenuItem"
                            type="button"
                            disabled={!canConfigure || reconnecting === s.id}
                            title={canConfigure ? "Force a fresh connection now (new session, re-subscribe)" : "Requires configure permission"}
                            onClick={() => handleReconnect(s)}
                          >
                            {reconnecting === s.id ? "Reconnecting…" : "Reconnect"}
                          </button>
                          <div className="gwProfileDivider" />
                          <button
                            className="gwProjectMenuItem gwProjectMenuDanger"
                            type="button"
                            disabled={!canConfigure}
                            title={canConfigure ? undefined : "Requires configure permission"}
                            onClick={() => { setMenuOpen(null); setDeleteTarget(s); }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {showCreate && (
        <ServerModal onClose={() => setShowCreate(false)} onSaved={fetchAll} />
      )}
      {editing && (
        <ServerModal editing={editing} onClose={() => setEditing(null)} onSaved={fetchAll} />
      )}

      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Server</div>
                <div className="gwModalSubtitle">Bound tag sources will be cleared.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Remove server <strong>{deleteTarget.name || deleteTarget.id}</strong>? Any tags bound to this server will lose their source and go QualityBad.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove Server"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
